import React, { Suspense, useEffect, useMemo, useState } from 'react';

import { Badge, Box, Typography } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { Notifications } from '@mui/icons-material';
import Breadcrumps from 'components/Breadcrumps';
import Container from 'components/Container';
import Spinner from 'components/Spinner';
import { formatNumberToCurrency, formatZillions, numberWithCommas } from 'helpers/utils';
import useUrlParams from 'hooks/useUrlParams';
import { useSelector, useDispatch } from 'react-redux';
import * as actions from 'store/actions';
import clsx from 'clsx';
import AGGridTable from '../AGGrid';

const makeBundleId = (portfolioIds) => `Page1-PortfolioIds${portfolioIds}`;

const useLocalStyles = makeStyles((theme) => ({
  icon: {
    color: '#CCCCCC'
  },
  root: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 500,
    padding: theme.spacing(6)
  },
  badge: {
    minWidth: theme.spacing(2.2),
    height: theme.spacing(2.2),
    width: theme.spacing(2.2)
  }
}));
import useStyles from 'styles/tables';
import Grow from '@mui/material/Grow';
import useRouter from '../../hooks/useRouter';
import AGGridHeader from '../AGGrid/AGGridHeader';
import { getUserTableViews } from 'store/actions';
import Tabs from '../../components/Tabs/Tabs';
import TabPanel from '../../components/Tabs/TabPanel';
import CoreFilters from '../CoreFilters';
import Paper from '@mui/material/Paper';
import Grid from '@mui/material/Grid';
import ApexChart from '../../components/Charts/ApexChart';
import { getLeaseReports } from '../../store/actions/reportsAction';

const Tenants = () => {
  const classes = useStyles();
  const localClasses = useLocalStyles();
  const dispatch = useDispatch();
  const { portfolioIds, setPortfolios } = useUrlParams();
  const { loaded, error, tenants } = useSelector((state) => state.tenants.tenants);
  const router = useRouter();
  const [tableSettingsChanged, setTableSettingsChanged] = React.useState(false);
  const { data: tableViews } = useSelector((state) => state.user.tablesViewState);
  const [tab, setTab] = useState(() => 0);
  const { loaded: reportsLoaded, data: lease_reports } = useSelector(
    (state) => state.reports.lease_reports
  );
  const [filter, setFilter] = useState({});

  // Default chart height
  const CHART_HEIGHT = 420; // Increased height
  const TEXT_COLOR = '#333333';

  const baseChartOptions = {
    chart: {
      foreColor: TEXT_COLOR,
      toolbar: { show: false }
    },
    xaxis: {
      labels: {
        style: {
          colors: [TEXT_COLOR],
          fontSize: '12px'
        }
      },
      title: {
        style: {
          color: TEXT_COLOR,
          fontSize: '14px'
        }
      },
      axisBorder: {
        show: true,
        color: '#ccc'
      },
      axisTicks: {
        show: true,
        color: '#ccc'
      }
    },
    yaxis: {
      labels: {
        style: {
          colors: [TEXT_COLOR],
          fontSize: '12px'
        }
      },
      title: {
        style: {
          color: TEXT_COLOR,
          fontSize: '14px'
        }
      }
    },
    grid: {
      borderColor: '#e0e0e0',
      strokeDashArray: 4,
      row: {
        colors: undefined,
        opacity: 0.5
      },
      column: {
        colors: undefined,
        opacity: 0.5
      }
    },
    legend: {
      labels: { colors: TEXT_COLOR },
      fontSize: '13px'
    },
    tooltip: {
      theme: 'light' // Use light theme for dark text on light background
    },
    dataLabels: {
      // Default data label settings (can be overridden)
      enabled: false, // Disable by default, enable per chart if needed
      style: {
        fontSize: '12px'
        // Avoid setting default color here, let individual charts handle contrast
      }
    }
    // Define a default color palette if desired (optional)
    // colors: ['#008FFB', '#00E396', '#FEB019', '#FF4560', '#775DD0'],
  };

  const chartBoxSx = {
    border: (theme) => `1px solid ${theme.palette.divider}`, // Use theme's divider color
    borderRadius: '4px', // Optional: Add border radius (match Paper's default or customize)
    p: 2, // Optional: Add padding inside the border (theme.spacing(2))
    // Keep layout styles from className if needed, or redefine here
    height: '100%',
    display: 'flex',
    flexDirection: 'column'
  };

  const [colDefs, setColDefs] = useState([
    {
      colId: 'name',
      headerName: 'Tenant',
      field: 'companyname',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader,
    },
    {
      colId: 'assetName',
      headerName: 'Asset',
      field: 'assetname',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Industry',
      field: 'industry',
      filter: 'agSetColumnFilter',
      chartDataType: 'category',
      enableRowGroup: true,
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Contract Start',
      field: 'contractstartdate',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      chartDataType: 'category',
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Portfolio',
      field: 'portfolioname',
      enableRowGroup: true,
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Contract End',
      field: 'contractenddate',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      chartDataType: 'category',
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'GLA (m2)',
      field: 'totalgla',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => numberWithCommas(params.value),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Current Year Rent',
      field: 'totalrent',
      filter: 'agNumberColumnFilter',
      cellRenderer: (params) => numberWithCommas(params.value),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Rent / SQM / Month',
      filter: 'agNumberColumnFilter',
      field: 'rentpersqm',
      cellRenderer: (params) => formatNumberToCurrency(params.value),
      chartDataType: 'series',
      aggFunc: 'sum',
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'In-place',
      field: 'inplace',
      filter: 'agSetColumnFilter',
      cellRenderer: (props) => (
        <span style={{ color: props.value === 'No' ? '#AAAAAA' : '#01B231' }}>{props.value}</span>
      ),
      enableRowGroup: true,
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Next Indexation Date',
      field: 'indexationdate',
      cellDataType: 'date',
      filter: 'agDateColumnFilter',
      headerComponent: AGGridHeader,
      width: 250
    },
    {
      headerName: 'Next Indexation',
      field: 'indexation',
      filter: 'agNumberColumnFilter',
      valueFormatter: (params) => (params.value === 0 ? '' : `${params.value}%`),
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Comment',
      field: 'comment',
      filter: 'agSetColumnFilter',
      headerComponent: AGGridHeader,
    },
    {
      headerName: 'Notifications',
      field: 'notifs',
      sortable: false,
      cellRenderer: (params) => (
        <Badge
          badgeContent={params.value}
          color="error"
          overlap="circular"
          classes={{ badge: localClasses.badge }}
        >
          <Notifications className={localClasses.icon} />
        </Badge>
      ),
      headerComponent: AGGridHeader,
    }
  ]);

  function monthDiff(d1, d2) {
    let months;
    months = (d2.getFullYear() - d1.getFullYear()) * 12;
    months -= d1.getMonth();
    months += d2.getMonth();
    if (d1.getDate() > d2.getDate()) {
      months--;
    }

    return months <= 0 ? 0 : months;
  }

  useEffect(() => {
    dispatch(actions.getLeaseReports(filter.assetIds, filter.portfolioIds, filter.assetTypes));
  }, [filter]);

  useEffect(() => {
    const bundleId = makeBundleId(portfolioIds);
    dispatch(actions.getTenants(portfolioIds, bundleId, [], true));
  }, [portfolioIds]);

  useEffect(() => {
    if (tableViews.length === 0) {
      dispatch(getUserTableViews());
    }
  }, []);

  const tenantsTabletViews = useMemo(
    () => tableViews.filter((it) => it.tableviewtypename === TENANTS_TABLE_VIEW_TYPE_NAME),
    [tableViews]
  );

  if (loaded && error) {
    return (
      <div className={localClasses.root}>
        <Typography color="error" variant="h5">
          {error.substr(0, 45)}
        </Typography>
      </div>
    );
  }

  const onCellClick = (e) => {
    if (e.column.colId === 'name') {
      // cell is from non-select column
      router.push(`/Asset/${e.data.assetid}/Tenant/${e.data.id}`, {
        from: 'Details'
      });
    }

    if (e.column.colId === 'assetName') {
      // cell is from non-select column
      router.push(`/Asset/${e.data.assetid}/Details`);
    }
  };

  const handleTabChange = (_, newValue) => {
    setTab(newValue);
  };

  return (
    <Container>
      <Suspense fallback={<Spinner mini />}>
        <Grow in>
          <Box className={clsx(classes.containerLarge)}>
            <Box className={classes.header}>
              <Typography className={classes.title}>{`Leases (${
                tenants?.length || 0
              })`}</Typography>
            </Box>
            <Tabs
              value={tab}
              handleChange={handleTabChange}
              tabNames={['Table', 'Charts']}
            />
            <TabPanel value={tab} index={0}>
              <Box className={clsx(classes.table)}>
                <AGGridTable
                  data={
                    loaded &&
                    tenants.map((item) => {
                      let months;
                      let endDate = new Date(item.contractenddate);
                      let now = new Date();
                      if (endDate < now && endDate.getFullYear() === now.getFullYear()) {
                        months = monthDiff(new Date(new Date().getFullYear(), 0, 1), endDate);
                      } else {
                        months = monthDiff(new Date(now.getFullYear(), 0, 1), now);
                      }

                      let rent;
                      if (months > 0 && item.totalgla > 0) {
                        rent = item.totalrent / item.totalgla / months / 2;
                      }

                      return {
                        ...item,
                        contractstartdate:  parseDate(item.contractstartdate),
                        contractenddate:  parseDate(item.contractenddate),
                        rentpersqm: rent,
                        inplace: item.inplace
                          ? Date.parse(item.contractenddate) < new Date().getTime()
                            ? 'Expired'
                            : 'Yes'
                          : 'No'
                      };
                    })
                  }
                  colDefs={colDefs}
                  classes={classes}
                  onCellClicked={onCellClick}
                  rowGroupPanelShow={'always'}
                  groupDisplayType={'multipleColumns'}
                  tableSettingsChanged={tableSettingsChanged}
                  onTableSettingsChanged={() => setTableSettingsChanged(true)}
                  tableViewTypeName={TENANTS_TABLE_VIEW_TYPE_NAME}
                  tableViews={tenantsTabletViews}
                ></AGGridTable>
              </Box>
            </TabPanel>

            <TabPanel value={tab} index={1}>
              <Box
                sx={{
                  flexGrow: 1,
                  py: 2,
                  minHeight: '75vh'
                }}
              >
                <CoreFilters filtersUpdated={setFilter} targetAssetIdsUpdated={() => {}} />
                <Paper
                  sx={{
                    p: 4,
                    width: '100%',
                    height: '100%'
                  }}
                >
                  {reportsLoaded ? (
                    <Grid container spacing={4} alignItems="stretch">
                      {/* --- Chart 1: Leases coutn by asset manager (Pie) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            Leases Count by Asset Manager
                          </Typography>
                          <div style={{ width: '100%' }}>
                            <ApexChart
                              type="pie"
                              width="100%"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                labels:
                                  (lease_reports?.leasesCountByAssetManager?.dataMap ?? []).map((item) => item[0]) ??
                                  [],
                                legend: {
                                  ...baseChartOptions.legend,
                                  position: 'right'
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    formatter: (value) => {
                                      const numValue = parseFloat(value);
                                      if (isNaN(numValue)) return '';
                                      return typeof formatZillions === 'function'
                                        ? formatZillions(numValue)
                                        : numValue.toFixed(0);
                                    },
                                    title: { formatter: (seriesName) => seriesName }
                                  }
                                },
                                dataLabels: {
                                  enabled: true,
                                  formatter: (val, opts) => {
                                    const numVal = parseFloat(val);
                                    return isNaN(numVal) ? '' : `${numVal.toFixed(1)}%`;
                                  },
                                  style: {
                                    ...baseChartOptions.dataLabels.style,
                                    colors: undefined // Let ApexCharts handle contrast
                                  },
                                  dropShadow: {
                                    enabled: true,
                                    top: 1,
                                    left: 1,
                                    blur: 1,
                                    color: '#000',
                                    opacity: 0.45
                                  }
                                },
                                responsive: [
                                  {
                                    breakpoint: 600,
                                    options: {
                                      chart: {
                                        width: '100%',
                                        height: CHART_HEIGHT * 0.8
                                      },
                                      legend: { position: 'bottom' }
                                    }
                                  }
                                ],
                              }}
                              series={
                                (lease_reports?.leasesCountByAssetManager?.dataMap ?? []).map((item) => item[1]) ?? []
                              }
                            />

                          </div>
                        </Box>
                      </Grid>

                      {/* --- Chart 2: leases by month (Bar) --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            Leases Count per Month
                          </Typography>
                          <div style={{ width: '100%', overflowY: 'auto' }}>
                            <ApexChart
                              type="bar"
                              width="3000px"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                xaxis: {
                                  ...baseChartOptions.xaxis,
                                  type: 'category' // X-axis is categorical
                                },
                                yaxis: {
                                  ...baseChartOptions.yaxis,
                                  labels: {
                                    ...baseChartOptions.yaxis.labels,
                                    // Format Y-axis to integer (0 decimals)
                                    formatter: (value) => {
                                      if (typeof value !== 'number') {
                                        return value;
                                      }
                                      // This will format the number with commas (e.g., 1000 -> "1,000")
                                      // and ensure it has no decimal places.
                                      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });

                                    }
                                  }
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    // Format tooltip to integer (0 decimals)
                                    formatter: (value) => {
                                      if (typeof value !== 'number') {
                                        return value;
                                      }
                                      // This will format the number with commas (e.g., 1000 -> "1,000")
                                      // and ensure it has no decimal places.
                                      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });
                                    }
                                  }
                                },
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={[
                                {
                                  name: 'Count',
                                  data: sortYearMonthData((lease_reports?.leasesCountByMonthYear?.dataMap ?? []))
                                    .map((item) => ({ x: item[0], y: item[1] }))
                                }
                              ]}
                            />

                          </div>
                        </Box>
                      </Grid>

                      {/* --- Chart 3: Vacant area --- */}
                      <Grid item xs={12} md={6}>
                        {/* Apply sx prop to the Box */}
                        <Box className={localClasses.chartContainer} sx={chartBoxSx}>
                          <Typography className={localClasses.chartTitle}>
                            Vacant GLA by Month
                          </Typography>
                          <div style={{ width: '100%', overflowY: 'auto' }}>
                            <ApexChart
                              type="bar"
                              width="3000px"
                              height={CHART_HEIGHT}
                              options={{
                                ...baseChartOptions, // Apply base options
                                xaxis: {
                                  ...baseChartOptions.xaxis,
                                  type: 'category' // X-axis is categorical
                                },
                                yaxis: {
                                  ...baseChartOptions.yaxis,
                                  labels: {
                                    ...baseChartOptions.yaxis.labels,
                                    // Format Y-axis to integer (0 decimals)
                                    formatter: (value) => {
                                      if (typeof value !== 'number') {
                                        return value;
                                      }
                                      // This will format the number with commas (e.g., 1000 -> "1,000")
                                      // and ensure it has no decimal places.
                                      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });

                                    }
                                  }
                                },
                                tooltip: {
                                  ...baseChartOptions.tooltip,
                                  y: {
                                    // Format tooltip to integer (0 decimals)
                                    formatter: (value) => {
                                      if (typeof value !== 'number') {
                                        return value;
                                      }
                                      // This will format the number with commas (e.g., 1000 -> "1,000")
                                      // and ensure it has no decimal places.
                                      return value.toLocaleString(undefined, { maximumFractionDigits: 0 });

                                    }
                                  }
                                },
                                legend: {
                                  position: 'right'
                                }
                              }}
                              series={[
                                {
                                  name: 'Count',
                                  data: sortYearMonthData((lease_reports?.vacantGlaCountByMonthYear?.dataMap ?? []))
                                    .map((item) => ({ x: item[0], y: item[1] }))
                                }
                              ]}
                            />

                          </div>
                        </Box>
                      </Grid>
                    </Grid>
                  ) : (
                    <Box
                      sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%'
                      }}
                    >
                      <Spinner />
                    </Box>
                  )}
                </Paper>
              </Box>
            </TabPanel>
          </Box>
        </Grow>
      </Suspense>
    </Container>
  );
};

function parseDate(value) {
  const result = new Date(Date.parse(value, 'yyyy-MM-dd'));
  result.setHours(0, 0, 0, 0);
  return result;
}

function sortYearMonthData(data) {
  const monthOrder = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const res = [...data];
  res.sort((a, b) => {
    const partsA = a[0].split(' ');
    const partsB = b[0].split(' ');

    const monthA = partsA[0];
    const yearA = parseInt(partsA[1], 10);

    const monthB = partsB[0];
    const yearB = parseInt(partsB[1], 10);

    if (yearA !== yearB) {
      return yearA - yearB;
    }

    const monthIndexA = monthOrder.indexOf(monthA);
    const monthIndexB = monthOrder.indexOf(monthB);

    return monthIndexA - monthIndexB;
  });

  return res;
}

export default Tenants;

const TENANTS_TABLE_VIEW_TYPE_NAME = 'Tenants';